import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { money } from "@/components/shared";
import { Compass, CalendarHeart, Plus, Trash2, Loader2 } from "lucide-react";

function ContractEditor({ clientId, initial, onSaved }) {
  const [items, setItems] = useState(initial);
  useEffect(() => { setItems(initial); }, [initial]);
  const save = useMutation({
    mutationFn: async () => (await api.put(`/agent/clients/${clientId}/contract`, { items: items.filter((i) => i.label.trim()) })).data,
    onSuccess: () => { onSaved(); toast.success("Contract timeline saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const upd = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Card className="p-5 rounded-2xl" data-testid="contract-editor">
      <h3 className="font-heading font-semibold flex items-center gap-2"><Compass className="h-4 w-4 text-primary" /> Under-contract timeline</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-4">The client sees this once their journey reaches Under Contract. The Closing date also sets their home anniversary.</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 p-2 rounded-xl bg-secondary/60" data-testid={`contract-row-${i}`}>
            <input type="checkbox" checked={!!it.done} onChange={(e) => upd(i, "done", e.target.checked)} className="h-4 w-4 accent-primary" data-testid={`contract-done-${i}`} />
            <Input className="h-9 flex-1 min-w-[160px]" value={it.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`contract-label-${i}`} />
            <Input type="date" className="h-9 w-40" value={it.due_date || ""} onChange={(e) => upd(i, "due_date", e.target.value)} data-testid={`contract-date-${i}`} />
            <Input className="h-9 w-full sm:w-48" placeholder="Note (optional)" value={it.note || ""} onChange={(e) => upd(i, "note", e.target.value)} />
            <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`contract-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => setItems([...items, { key: "", label: "", due_date: "", done: false, note: "" }])} data-testid="contract-add-btn"><Plus className="h-3.5 w-3.5" /> Add date</Button>
        <Button size="sm" className="rounded-full" onClick={() => save.mutate()} disabled={save.isPending} data-testid="contract-save-btn">{save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save timeline"}</Button>
      </div>
    </Card>
  );
}

function AnniversaryEditor({ clientId, closingDate, updates, onSaved }) {
  const [f, setF] = useState({ estimated_value: "", value_low: "", value_high: "", note: "" });
  const num = (v) => (v ? Number(v) : null);
  const setDate = useMutation({
    mutationFn: async (closing_date) => (await api.put(`/agent/clients/${clientId}/closing-date`, { closing_date })).data,
    onSuccess: () => { onSaved(); toast.success("Closing date saved"); },
  });
  const add = useMutation({
    mutationFn: async () => (await api.post(`/agent/clients/${clientId}/anniversary`, { ...f, estimated_value: num(f.estimated_value), value_low: num(f.value_low), value_high: num(f.value_high) })).data,
    onSuccess: () => { onSaved(); setF({ estimated_value: "", value_low: "", value_high: "", note: "" }); toast.success("Value update added"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (eid) => (await api.delete(`/agent/clients/${clientId}/anniversary/${eid}`)).data, onSuccess: onSaved });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card className="p-5 rounded-2xl" data-testid="anniversary-editor">
      <h3 className="font-heading font-semibold flex items-center gap-2"><CalendarHeart className="h-4 w-4 text-primary" /> Home anniversary</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-4">Shown to the client after Keys. They get an email each year on this date.</p>
      <Label className="text-xs">Closing date</Label>
      <Input type="date" defaultValue={closingDate} key={closingDate} onBlur={(e) => e.target.value !== closingDate && setDate.mutate(e.target.value)} className="h-9 w-44 mt-1 mb-4" data-testid="closing-date-input" />
      <p className="text-xs font-semibold mb-2">Add an estimated value update (estimate only, not an appraisal)</p>
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" placeholder="Estimate $" value={f.estimated_value} onChange={set("estimated_value")} className="h-9" data-testid="anniv-value-input" />
        <Input type="number" placeholder="Low $" value={f.value_low} onChange={set("value_low")} className="h-9" />
        <Input type="number" placeholder="High $" value={f.value_high} onChange={set("value_high")} className="h-9" />
      </div>
      <Textarea placeholder="A short note (e.g. what nearby homes sold for)" value={f.note} onChange={set("note")} className="rounded-xl mt-2" data-testid="anniv-note-input" />
      <Button size="sm" className="rounded-full mt-2" onClick={() => add.mutate()} disabled={(!f.estimated_value && !f.note.trim()) || add.isPending} data-testid="anniv-add-btn">Add update</Button>
      <div className="mt-4 space-y-2">
        {updates.map((u) => (
          <div key={u.id} className="flex items-start gap-2 p-3 rounded-xl bg-secondary text-sm" data-testid={`anniv-row-${u.id}`}>
            <div className="flex-1 min-w-0"><p className="font-medium">{u.estimated_value ? money(u.estimated_value) : "Note"} <span className="text-xs text-muted-foreground">· {new Date(u.created_at).toLocaleDateString()}</span></p>{u.note && <p className="text-xs text-muted-foreground">{u.note}</p>}</div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del.mutate(u.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ClosingManager({ clientId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["closing", clientId], queryFn: async () => (await api.get(`/agent/clients/${clientId}/closing`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["closing", clientId] });
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <ContractEditor clientId={clientId} initial={data.contract} onSaved={refresh} />
      <AnniversaryEditor clientId={clientId} closingDate={data.closing_date} updates={data.anniversary_updates} onSaved={refresh} />
    </div>
  );
}
